import { useState } from 'react';
import Logo from '@/components/ui/Logo.jsx';
import Icon from '@/components/ui/Icon.jsx';
import { useAuth } from '@/contexts/AuthContext.jsx';

const PILLARS = [
  {
    icon: 'sparkles',
    title: 'AI-Accelerated Research',
    body: 'A first-pass profile is assembled in minutes across public and licensed sources.',
  },
  {
    icon: 'link',
    title: 'Traceable and Verifiable',
    body: 'Every finding carries the citation behind it, open for review.',
  },
  {
    icon: 'list',
    title: 'Structured and Actionable',
    body: 'Consistent dossier sections that carry straight into a decision.',
  },
  {
    icon: 'activity',
    title: 'Continuous Monitoring',
    body: 'Material changes on saved subjects raise an alert.',
  },
];

const TRUST_POINTS = [
  { icon: 'fileCheck', text: 'Source-backed findings, reviewable line by line' },
  { icon: 'bellDot', text: 'Material changes surfaced as alerts, not noise' },
  { icon: 'shield', text: 'Encrypted, access-controlled research workspace' },
];

const AUDIENCES = [
  {
    icon: 'landmark',
    title: 'Wealth Managers',
    body: 'Understand clients, prospects, wealth, business interests, relationships, reputation, and potential risks before onboarding or deepening a relationship.',
  },
  {
    icon: 'scale',
    title: 'Legal and Professional Services',
    body: 'Conduct faster background, client, counterparty, executive, and reputational due diligence with evidence that can be reviewed and verified.',
  },
  {
    icon: 'shieldCheck',
    title: 'Corporate Compliance Teams',
    body: 'Support KYC, EDD, third-party risk, integrity reviews, and ongoing monitoring through structured and consistently documented intelligence.',
  },
  {
    icon: 'graduation',
    title: 'Universities and Nonprofits',
    body: 'Research prospective donors, understand sources of wealth and affiliations, identify reputational concerns.',
  },
];

export default function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(err?.message || 'Invalid email or password.');
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-split">

        {/* Marketing panel */}
        <div style={{ padding: '64px 56px 48px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 56 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Logo size={30} />
              <span style={{ fontFamily: 'var(--font-brand)', fontWeight: 600, fontSize: 19, letterSpacing: '-0.01em', color: 'var(--brand-purple)' }}>
                Diligencify
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 520 }}>
              <h1 style={{ margin: 0, fontSize: 44, fontWeight: 600, lineHeight: 1.12, letterSpacing: '-0.03em', color: 'var(--fg)', textWrap: 'pretty' }}>
                Decision-ready due diligence, in minutes.
              </h1>
              <p style={{ margin: 0, fontSize: 16, lineHeight: 1.6, color: 'var(--fg-muted)', textWrap: 'pretty' }}>
                AI-powered deep intelligence on individuals and organizations. Every finding is cited to its source, structured for review, and kept current after delivery.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 560 }}>
              <div className="login-pillars">
                {PILLARS.map((p) => (
                  <div className="login-pillar" key={p.title}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <Icon name={p.icon} size={16} style={{ color: 'var(--brand-purple)', flex: 'none' }} />
                      <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--fg)' }}>{p.title}</span>
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--fg-muted)' }}>{p.body}</div>
                  </div>
                ))}
              </div>
              <div style={{ paddingTop: 20, marginTop: 20, fontSize: 13, lineHeight: 1.6, color: 'var(--fg-muted)', borderTop: '1px solid rgba(51,30,109,0.12)' }}>
                Coverage spans wealth, business interests, relationships, litigation, adverse media, sanctions and PEP status, and reputation.
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 520 }}>
            {TRUST_POINTS.map((t) => (
              <div key={t.text} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Icon name={t.icon} size={15} style={{ marginTop: 2, flex: 'none', color: 'var(--brand-purple)' }} />
                <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--fg-muted)' }}>{t.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Auth panel */}
        <div className="login-auth" style={{ padding: '64px 56px 48px', display: 'flex', flexDirection: 'column', gap: 40 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 13, color: 'var(--fg-muted)' }}>
            Need access?
            <a
              href="https://www.diligencify.com/Contact.html"
              style={{
                marginLeft: 6,
                fontWeight: 500,
                color: 'var(--brand-purple)',
                textDecoration: 'none',
                borderBottom: '1px solid var(--brand-purple-100)',
              }}
            >
              Request an account
            </a>
          </div>

          <form onSubmit={handleSubmit} className="login-card">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 9, fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--fg)' }}>
                <Icon name="lock" size={18} style={{ color: 'var(--brand-purple)' }} />
                Sign in
              </h2>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--fg-muted)' }}>Access the intelligence platform</p>
            </div>

            {error && (
              <div style={{
                background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 10,
                padding: '10px 14px', fontSize: 13, color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <Icon name="alert" size={14} />
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>Work email</span>
                <input
                  className="login-input"
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  placeholder="you@firm.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
                  Password
                  <a href="/forgot-password" style={{ fontWeight: 400, fontSize: 12, color: 'var(--fg-muted)', textDecoration: 'none' }}>Forgot?</a>
                </span>
                <input
                  className="login-input"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  placeholder="••••••••••"
                  style={{ letterSpacing: '0.08em' }}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
              <button type="submit" className="login-submit" disabled={loading}>
                {loading ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                    Signing in…
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Who it is built for */}
      <div className="login-audience">
        <div className="login-audience-inner">
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)' }}>
            Who it is built for
          </div>
          <div className="login-audience-grid">
            {AUDIENCES.map((a) => (
              <div className="login-audience-item" key={a.title}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <Icon name={a.icon} size={18} style={{ color: 'var(--brand-purple)' }} />
                  <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--fg)' }}>{a.title}</span>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--fg-muted)', textWrap: 'pretty' }}>{a.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
